import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Inbox, Check, X } from "lucide-react";
import Logo from "../components/Logo";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosAuth = () => axios.create({ baseURL: API, withCredentials: true });

const LOCALES = { en: "en-GB", fr: "fr-FR", nl: "nl-NL", ar: "ar" };

function formatApiErrorDetail(detail) {
  if (detail == null) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function formatDate(iso, locale) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? String(iso).slice(0, 16).replace("T", " ") : d.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
}

function Badge({ status }) {
  const tone =
    status === "paid" || status === "completed" || status === "approved"
      ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
      : status === "failed" || status === "expired" || status === "refunded" || status === "rejected"
        ? "bg-red-500/10 text-red-300 border-red-500/30"
        : "bg-gold/10 text-gold-soft border-gold/30";
  return (
    <span className={`inline-block rounded-full border px-2.5 py-0.5 font-mono2 text-[10px] uppercase ${tone}`}>
      {status || "new"}
    </span>
  );
}

function DataTable({ testid, columns, rows, renderRow, emptyLabel }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-white/15 p-10 text-center text-slate-500" data-testid={`${testid}-empty`}>
        <Inbox size={28} className="mx-auto mb-3 text-slate-600" />
        {emptyLabel}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-white/10">
      <table className="w-full min-w-[860px] text-sm" data-testid={testid}>
        <thead>
          <tr className="bg-white/[0.04]">
            {columns.map((c) => (
              <th key={c} className="p-4 font-mono2 text-[10px] uppercase text-slate-400">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} data-testid={`${testid}-row-${i}`} className="border-t border-white/10 hover:bg-white/[0.02]">
              {renderRow(r, i)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  const { t, lang } = useLanguage();
  const locale = LOCALES[lang] || "en-GB";
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("drivers");
  const [drivers, setDrivers] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [payments, setPayments] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [deciding, setDeciding] = useState(null);

  const loadAll = async () => {
    const api = axiosAuth();
    try {
      const [d, c, p] = await Promise.all([
        api.get("/drivers"), api.get("/contacts"), api.get("/payments")
      ]);
      setDrivers(d.data);
      setContacts(c.data);
      setPayments(p.data);
    } catch (e) {
      if (e.response?.status === 401) {
        setUser(null);
        return;
      }
      toast.error(t.errors.generic);
    }
  };

  const decideDriver = async (id, status) => {
    setDeciding(id);
    try {
      await axiosAuth().post(`/drivers/${id}/status`, { status });
      setDrivers((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(t.admin.actions.decisionSent);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t.errors.generic);
    } finally {
      setDeciding(null);
    }
  };

  useEffect(() => {
    axiosAuth()
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) loadAll();
  }, [user]);

  const onLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axiosAuth().post("/auth/login", { email, password });
      setUser(data);
      setPassword("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onLogout = async () => {
    try {
      await axiosAuth().post("/auth/logout");
    } finally {
      setUser(null);
    }
  };

  if (user === undefined) {
    return <main className="pt-[72px] min-h-screen flex items-center justify-center"><p className="font-mono2 text-xs uppercase text-slate-500">{t.admin.loading}</p></main>;
  }

  if (!user) {
    return (
      <main className="pt-[72px] min-h-screen flex items-center justify-center px-5">
        <form data-testid="admin-login-form" onSubmit={onLogin} noValidate className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0F1015] p-8 lg:p-10">
          <div className="flex justify-center"><Logo /></div>
          <h1 className="mt-8 text-center font-display text-xl font-bold uppercase">{t.admin.loginTitle}</h1>
          <p className="mt-2 text-center text-sm text-slate-400">{t.admin.loginSub}</p>
          <div className="mt-7 space-y-4">
            <div>
              <label htmlFor="admin-email" className="mb-2 block font-mono2 text-[11px] uppercase text-slate-400">{t.admin.email}</label>
              <input id="admin-email" data-testid="admin-email-input" type="email" className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-2 block font-mono2 text-[11px] uppercase text-slate-400">{t.admin.password}</label>
              <input id="admin-password" data-testid="admin-password-input" type="password" className="field-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            </div>
            <button type="submit" data-testid="admin-login-button" disabled={busy} className="btn-gold w-full text-xs font-mono2 uppercase disabled:opacity-60">
              {busy ? "…" : t.admin.signIn}
            </button>
          </div>
        </form>
      </main>
    );
  }

  const tabs = [
    { id: "drivers", label: `${t.admin.tabs.drivers}${drivers ? ` (${drivers.length})` : ""}` },
    { id: "contacts", label: `${t.admin.tabs.requests}${contacts ? ` (${contacts.length})` : ""}` },
    { id: "payments", label: `${t.admin.tabs.payments}${payments ? ` (${payments.length})` : ""}` }
  ];

  return (
    <main data-testid="admin-dashboard" className="pt-[72px] min-h-screen">
      <div className="mx-auto max-w-7xl px-5 lg:px-10 py-14">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="eyebrow">MIRAL GROUP</p>
            <h1 className="mt-2 font-display text-2xl lg:text-3xl font-bold uppercase">{t.admin.title}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" data-testid="admin-refresh-button" onClick={loadAll} className="btn-outline !py-2.5 !px-5 text-[11px] font-mono2 uppercase">
              <RefreshCw size={14} /> {t.admin.refresh}
            </button>
            <button type="button" data-testid="admin-logout-button" onClick={onLogout} className="btn-outline !py-2.5 !px-5 text-[11px] font-mono2 uppercase">
              <LogOut size={14} /> {user.email}
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              type="button"
              data-testid={`admin-tab-${tb.id}`}
              onClick={() => setTab(tb.id)}
              className={`rounded-full border px-5 py-2.5 font-mono2 text-[11px] uppercase transition-all duration-300 ${
                tab === tb.id ? "border-gold/60 bg-gold/10 text-gold-soft" : "border-white/10 text-slate-300 hover:border-white/30"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "drivers" && (
            <DataTable
              testid="admin-drivers-table"
              columns={t.admin.cols.drivers}
              rows={drivers || []}
              emptyLabel={t.admin.empty}
              renderRow={(r, i) => (
                <>
                  <td className="p-4 font-mono2 text-xs text-slate-400">{formatDate(r.created_at, locale)}</td>
                  <td className="p-4 font-semibold text-white">{r.full_name}</td>
                  <td className="p-4 text-xs text-slate-300">{r.email}<br />{r.phone}</td>
                  <td className="p-4 text-slate-300">{r.city}</td>
                  <td className="p-4 font-mono2 text-xs text-slate-300">{r.driving_license_number}</td>
                  <td className="p-4 font-mono2 text-gold-soft">{r.experience_years}y</td>
                  <td className="p-4 text-xs text-slate-300">{r.languages}</td>
                  <td className="p-4"><Badge status={r.status} /></td>
                  <td className="p-4">
                    {r.status === "new" ? (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          data-testid={`admin-approve-driver-${i}`}
                          disabled={deciding === r.id}
                          onClick={() => decideDriver(r.id, "approved")}
                          className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3.5 py-1.5 text-[11px] font-mono2 uppercase text-emerald-300 transition-all duration-300 hover:bg-emerald-500/20 disabled:opacity-50"
                        >
                          <Check size={13} /> {t.admin.actions.approve}
                        </button>
                        <button
                          type="button"
                          data-testid={`admin-reject-driver-${i}`}
                          disabled={deciding === r.id}
                          onClick={() => decideDriver(r.id, "rejected")}
                          className="inline-flex items-center gap-1.5 rounded-full border border-red-500/40 bg-red-500/10 px-3.5 py-1.5 text-[11px] font-mono2 uppercase text-red-300 transition-all duration-300 hover:bg-red-500/20 disabled:opacity-50"
                        >
                          <X size={13} /> {t.admin.actions.reject}
                        </button>
                      </div>
                    ) : (
                      <span className="font-mono2 text-[10px] uppercase text-slate-500">—</span>
                    )}
                  </td>
                </>
              )}
            />
          )}
          {tab === "contacts" && (
            <DataTable
              testid="admin-contacts-table"
              columns={t.admin.cols.contacts}
              rows={contacts || []}
              emptyLabel={t.admin.empty}
              renderRow={(r) => (
                <>
                  <td className="p-4 font-mono2 text-xs text-slate-400">{formatDate(r.created_at, locale)}</td>
                  <td className="p-4 font-semibold text-white">{r.full_name}</td>
                  <td className="p-4 text-xs text-slate-300">{r.email}<br />{r.phone}</td>
                  <td className="p-4 font-mono2 text-xs text-gold-soft">{r.service_type}</td>
                  <td className="p-4 text-xs text-slate-300">{r.pickup_address}</td>
                  <td className="p-4 text-xs text-slate-300">{r.destination_address || "—"}</td>
                  <td className="p-4 text-xs text-slate-300">{r.pickup_datetime || "—"}</td>
                  <td className="p-4"><Badge status={r.payment_status || "unpaid"} /></td>
                </>
              )}
            />
          )}
          {tab === "payments" && (
            <DataTable
              testid="admin-payments-table"
              columns={t.admin.cols.payments}
              rows={payments || []}
              emptyLabel={t.admin.empty}
              renderRow={(r) => (
                <>
                  <td className="p-4 font-mono2 text-xs text-slate-400">{formatDate(r.created_at, locale)}</td>
                  <td className="p-4 font-mono2 text-xs text-slate-400">{(r.session_id || "").slice(0, 24)}…</td>
                  <td className="p-4 font-mono2 text-xs text-gold-soft">{r.contact_ref || "—"}</td>
                  <td className="p-4 font-mono2 text-gold-soft">{(r.amount / 100).toFixed(2)} {String(r.currency || "eur").toUpperCase()}</td>
                  <td className="p-4"><Badge status={r.payment_status || r.status} /></td>
                </>
              )}
            />
          )}
        </div>

        <p className="mt-10 text-center">
          <Link to="/" data-testid="admin-back-home-link" className="font-mono2 text-[11px] uppercase text-slate-500 hover:text-gold-soft transition-colors">
            {t.admin.backHome}
          </Link>
        </p>
      </div>
    </main>
  );
}
